import React, { useState, useCallback, useRef } from 'react';
import { useIntl, FormattedMessage, type MessageDescriptor } from 'react-intl';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '@api/client';
import Button from '@components/1-atoms/Button';
import { SNACKBAR_POSITION_BOTTOM_RIGHT } from '@components/2-molecules/Snackbar';
import { MESSAGE_BOX_VARIANT_ERROR } from '@components/2-molecules/MessageBox';
import { usePageTitle } from '@contexts/usePageTitle';
import useSnackbar from '@hooks/useSnackbar';
import { PageContainer, PageContent } from './Page.styled';
import {
  TabBar,
  Tab,
  ReadOnlyBanner,
  Description,
  InstructionsCard,
  InstructionsTextArea,
  InstructionsFooter,
  CharacterCount,
  ErrorContainer,
  LoadingContainer,
} from './Instructions.styled';

type InstructionsTab = 'personal' | 'team';

interface InstructionsResponse {
  content: string | null;
  characterCount: number;
  maxLength: number;
  updatedAt: string | null;
}

interface InstructionsPermissionsResponse {
  canEditTeam: boolean;
  isTeamOrganization: boolean;
}

const TEAM_QUERY_KEY = ['instructions', 'team'];
const PERSONAL_QUERY_KEY = ['instructions', 'personal'];
const PERMISSIONS_QUERY_KEY = ['instructionsPermissions'];
const TEAM_URL = '/api/v1/knowledge-base/instructions';
const PERSONAL_URL = '/api/v1/knowledge-base/personal/instructions';
const PERMISSIONS_URL = '/api/v1/knowledge-base/permissions';
const WARNING_THRESHOLD = 0.9;

const TEAM_SAVED_MESSAGE: MessageDescriptor = {
  id: 'instructions.saved.team',
  defaultMessage: 'Team instructions saved successfully',
};
const PERSONAL_SAVED_MESSAGE: MessageDescriptor = {
  id: 'instructions.saved.personal',
  defaultMessage: 'Personal instructions saved successfully',
};
const SAVE_ERROR_MESSAGE: MessageDescriptor = {
  id: 'instructions.saveError',
  defaultMessage: 'Failed to save instructions',
};

async function fetchJson<T>(url: string): Promise<T> {
  const response = await client.get<T>(url);
  return response.data;
}

function useSaveInstructions(
  url: string,
  queryKey: string[],
  savedMessage: MessageDescriptor,
  onSaved: () => void,
) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: async (newContent: string) => {
      const response = await client.put<InstructionsResponse>(url, { content: newContent });
      return response.data;
    },
    onSuccess: (responseData) => {
      queryClient.setQueryData(queryKey, responseData);
      onSaved();
      showSnackbar({
        text: intl.formatMessage(savedMessage),
        position: SNACKBAR_POSITION_BOTTOM_RIGHT,
      });
    },
    onError: () => {
      showSnackbar({
        text: intl.formatMessage(SAVE_ERROR_MESSAGE),
        position: SNACKBAR_POSITION_BOTTOM_RIGHT,
        variant: MESSAGE_BOX_VARIANT_ERROR,
      });
    },
  });
}

const InstructionsPage: React.FC = () => {
  const intl = useIntl();
  usePageTitle(
    intl.formatMessage({
      id: 'instructions.title',
      defaultMessage: 'Instructions',
    }),
    intl.formatMessage({
      id: 'instructions.help',
      defaultMessage:
        "Standing instructions the AI agent reads at the start of every task — your own, and your team's.",
    }),
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [activeTab, setActiveTab] = useState<InstructionsTab>('personal');
  const [localPersonalContent, setLocalPersonalContent] = useState<string | null>(null);
  const [localTeamContent, setLocalTeamContent] = useState<string | null>(null);

  const {
    data: permissions,
    isLoading: isPermissionsLoading,
    isError: isPermissionsError,
  } = useQuery<InstructionsPermissionsResponse>({
    queryKey: PERMISSIONS_QUERY_KEY,
    queryFn: () => fetchJson<InstructionsPermissionsResponse>(PERMISSIONS_URL),
    staleTime: 300_000,
  });

  const isTeamOrg = permissions?.isTeamOrganization ?? false;

  const {
    data: teamData,
    isLoading: isTeamLoading,
    isError: isTeamError,
  } = useQuery<InstructionsResponse>({
    queryKey: TEAM_QUERY_KEY,
    queryFn: () => fetchJson<InstructionsResponse>(TEAM_URL),
    staleTime: 60_000,
    enabled: isTeamOrg,
  });

  const {
    data: personalData,
    isLoading: isPersonalLoading,
    isError: isPersonalError,
  } = useQuery<InstructionsResponse>({
    queryKey: PERSONAL_QUERY_KEY,
    queryFn: () => fetchJson<InstructionsResponse>(PERSONAL_URL),
    staleTime: 60_000,
  });

  const canEditTeam = permissions?.canEditTeam ?? false;
  const isPersonalTab = activeTab === 'personal';

  const data = isPersonalTab ? personalData : teamData;
  const isLoading =
    isPermissionsLoading || (isPersonalTab ? isPersonalLoading : isTeamLoading);
  const isError =
    isPermissionsError || (isPersonalTab ? isPersonalError : isTeamError);
  const localContent = isPersonalTab ? localPersonalContent : localTeamContent;
  const setLocalContent = isPersonalTab ? setLocalPersonalContent : setLocalTeamContent;

  const content = localContent ?? data?.content ?? '';
  const isDirty = localContent !== null;

  const maxLength = data?.maxLength ?? 0;
  const charCount = content.length;
  const isOverLimit = maxLength > 0 && charCount > maxLength;
  const isNearLimit = maxLength > 0 && charCount > maxLength * WARNING_THRESHOLD;

  const isEditable = isPersonalTab || canEditTeam;

  const teamMutation = useSaveInstructions(
    TEAM_URL,
    TEAM_QUERY_KEY,
    TEAM_SAVED_MESSAGE,
    () => setLocalTeamContent(null),
  );
  const personalMutation = useSaveInstructions(
    PERSONAL_URL,
    PERSONAL_QUERY_KEY,
    PERSONAL_SAVED_MESSAGE,
    () => setLocalPersonalContent(null),
  );

  const mutation = isPersonalTab ? personalMutation : teamMutation;

  const handleContentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setLocalContent(e.target.value);
    },
    [setLocalContent],
  );

  const handleSave = useCallback(() => {
    if (!isDirty || isOverLimit || mutation.isPending || !isEditable) return;
    mutation.mutate(content);
  }, [content, isDirty, isOverLimit, mutation, isEditable]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    },
    [handleSave],
  );

  const formattedCount = charCount.toLocaleString();
  const formattedMax = maxLength.toLocaleString();

  const descriptionId = isPersonalTab
    ? 'instructions.description.personal'
    : 'instructions.description.team';

  const descriptionDefault = isPersonalTab
    ? 'Add personal instructions for the AI agent. These apply only to your conversations and take precedence over team instructions when they conflict.'
    : 'Add instructions that will be available to the AI agent at the start of every new conversation. These instructions are shared across your organization.';

  const placeholderId = isPersonalTab
    ? 'instructions.textareaPlaceholder.personal'
    : 'instructions.textareaPlaceholder.team';

  const placeholderDefault = isPersonalTab
    ? 'Enter your personal instructions for the AI agent...'
    : 'Enter instructions for the AI agent...';

  return (
    <PageContainer>
      <PageContent>
        {isTeamOrg && (
          <TabBar>
            <Tab
              $active={activeTab === 'personal'}
              onClick={() => setActiveTab('personal')}
              type="button"
            >
              {intl.formatMessage({
                id: 'instructions.tab.personal',
                defaultMessage: 'Personal',
              })}
            </Tab>
            <Tab
              $active={activeTab === 'team'}
              onClick={() => setActiveTab('team')}
              type="button"
            >
              {intl.formatMessage({
                id: 'instructions.tab.team',
                defaultMessage: 'Team',
              })}
            </Tab>
          </TabBar>
        )}

        {isLoading ? (
          <LoadingContainer>
            <FormattedMessage
              id="instructions.loading"
              defaultMessage="Loading instructions..."
            />
          </LoadingContainer>
        ) : isError ? (
          <ErrorContainer>
            <FormattedMessage
              id="instructions.loadError"
              defaultMessage="Failed to load instructions. Please try again later."
            />
          </ErrorContainer>
        ) : (
          <>
            <Description>
              <FormattedMessage id={descriptionId} defaultMessage={descriptionDefault} />
            </Description>

            {!isPersonalTab && !canEditTeam && (
              <ReadOnlyBanner>
                <FormattedMessage
                  id="instructions.description.teamReadOnly"
                  defaultMessage="These are your organization's shared instructions for the AI agent. Only admins can edit team instructions."
                />
              </ReadOnlyBanner>
            )}

            <InstructionsCard>
              <InstructionsTextArea
                ref={textareaRef}
                value={content}
                onChange={handleContentChange}
                onKeyDown={handleKeyDown}
                disabled={!isEditable}
                placeholder={intl.formatMessage({
                  id: placeholderId,
                  defaultMessage: placeholderDefault,
                })}
                spellCheck
              />

              <InstructionsFooter>
                <CharacterCount $warning={isNearLimit || isOverLimit}>
                  {intl.formatMessage(
                    {
                      id: 'instructions.characterCount',
                      defaultMessage: '{count} / {max} characters',
                    },
                    { count: formattedCount, max: formattedMax },
                  )}
                </CharacterCount>

                {isEditable && (
                  <Button
                    onClick={handleSave}
                    disabled={!isDirty || isOverLimit || mutation.isPending}
                    loading={mutation.isPending}
                    stretch={false}
                  >
                    {intl.formatMessage({
                      id: 'instructions.save',
                      defaultMessage: 'Save',
                    })}
                  </Button>
                )}
              </InstructionsFooter>
            </InstructionsCard>
          </>
        )}
      </PageContent>
    </PageContainer>
  );
};

export default InstructionsPage;
